import { UzButton, UzDialog, UzTextarea } from '@utazzo/ui';
import { Ban } from 'lucide-react';
import React, { useState } from 'react';

import { useRequestVoid } from '../use-void-approvals';

/**
 * "Request void" action for a won deal (accepted itinerary). Raises a void
 * request routed for approval; an admin requester voids immediately. Shown
 * only for ACCEPTED / CONVERTED_TO_BOOKING itineraries - the caller gates it.
 */
export const RequestVoidButton = ({ itineraryId }: { itineraryId: string }): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const request = useRequestVoid();

  const reasonTrimmed = reason.trim();
  const canSubmit = reasonTrimmed.length > 0 && !request.isPending;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    try {
      await request.mutateAsync({ itineraryId, reason: reasonTrimmed });
      setOpen(false);
      setReason('');
    } catch {
      // Error toast shown by the mutation hook.
    }
  };

  return (
    <>
      <UzButton
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        title="Reopen this won deal for an amendment"
      >
        <Ban className="mr-1.5 h-3.5 w-3.5" aria-hidden />
        Request void
      </UzButton>

      <UzDialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setOpen(false);
            setReason('');
          }
        }}
        title="Request to void this won deal"
        description="Use this to reopen a won deal for an itinerary amendment. Once approved, the contract is voided (CRM + e-sign), the lead returns to its previous active status, the itinerary goes back to Sent to customer, and the customer is emailed. Any amount already paid is retained for the new itinerary."
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <UzButton
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setReason('');
              }}
              disabled={request.isPending}
            >
              Cancel
            </UzButton>
            <UzButton
              onClick={() => void submit()}
              loading={request.isPending}
              disabled={!canSubmit}
            >
              Submit request
            </UzButton>
          </div>
        }
      >
        <UzTextarea
          name="voidReason"
          label="Reason for voiding (required)"
          placeholder="e.g. Customer wants to add 2 nights in Interlaken and change the Paris hotel"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={request.isPending}
        />
      </UzDialog>
    </>
  );
};
